import { useOutletContext, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { openTunnel, closeTunnel, getActiveSessions } from '../../services/DeviceDetailsAPI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faPlay, faStop, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Grid from '../../components/grid/Grid';
import { formatToBrowserTime } from '../../utils/timeFormat';
import './DeviceDetails.css';

const DeviceDetails = () => {
    const location = useLocation();

    const ctx = useOutletContext?.() || {};
    const qs = new URLSearchParams(location.search);
    const model = ctx?.model ?? qs.get('model') ?? '';
    const serialNumber = ctx?.serialNumber ?? qs.get('serialNumber') ?? '';

    const [activeSessions, setActiveSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openingTunnel, setOpeningTunnel] = useState(false);
    const [closingTunnel, setClosingTunnel] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [hasActiveSession, setHasActiveSession] = useState(false);

    const thingName = `${model}-${serialNumber}`;

    const headers = [
        { key: 'tunnelId', label: 'Tunnel ID', width: '20%' },
        { key: 'status', label: 'Status', width: '10%' },
        { key: 'description', label: 'Description', width: '15%' },
        { key: 'createdAt', label: 'Created At', width: '18%' },
        { key: 'lastUpdatedAt', label: 'Last Updated', width: '18%' },
        { key: 'tunnelArn', label: 'Tunnel ARN', width: '19%' },
    ];

    const mapApiResponseToGridData = (dataArray) => {
        if (!dataArray || dataArray.length === 0) {
            return [];
        }
        
        return (dataArray ?? []).map((item, index) => ({
            id: index + 1,
            tunnelId: item?.tunnelId ?? '',
            status: item?.status ?? '',
            description: item?.description ?? '',
            createdAt: item?.createdAt
                ? formatToBrowserTime(item.createdAt, false, false)
                : '',
            lastUpdatedAt: item?.lastUpdatedAt
                ? formatToBrowserTime(item.lastUpdatedAt, false, false)
                : '',
            tunnelArn: item?.tunnelArn ?? '',
        }));
    };

    const loadActiveSessions = async () => {
        try {
            setLoading(true);
            
            const thingName = `${model}-${serialNumber}`;
            const response = await getActiveSessions(thingName);
            const dataArray = Array.isArray(response) ? response : [];
            const mapped = mapApiResponseToGridData(dataArray);
            setActiveSessions(mapped);
        } catch (err) {
            console.error('Failed to load active sessions:', err);
            setActiveSessions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleStartSession = async () => {
        if (!model || !serialNumber) {
            toast.error('Missing model or serial number.');
            return;
        }

        try {
            setOpeningTunnel(true);
            const response = await openTunnel({ thingName });
            
            if (response?.tunnelId) {
                setCurrentSessionId(response.tunnelId);
                setHasActiveSession(true);
                localStorage.setItem(`session_${thingName}`, response.tunnelId);
                toast.success(`Session started for ${thingName}!`);
                
                await loadActiveSessions();
            }
        } catch (err) {
            console.error('Failed to start session:', err);
            toast.error('Failed to start session. Please try again.');
        } finally {
            setOpeningTunnel(false);
        }
    };

    const handleEndSession = async () => {
        if (!currentSessionId) {
            toast.error('No active session found to end.');
            return;
        }

        try {
            setClosingTunnel(true);
            await closeTunnel({ 
                thingName,
                tunnelId: currentSessionId 
            });
            
            setCurrentSessionId(null);
            setHasActiveSession(false);
            localStorage.removeItem(`session_${thingName}`);
            toast.success(`Session ended for ${thingName}!`);
            
            await loadActiveSessions();
        } catch (err) {
            console.error('Failed to end session:', err);
            toast.error('Failed to end session. Please try again.');
        } finally {
            setClosingTunnel(false);
        }
    };

    const handleRefresh = () => {
        loadActiveSessions();
    };

    useEffect(() => {
        if (!model || !serialNumber) {
            toast.error('Missing model or serial number.');
            return;
        }
        
        loadActiveSessions();
        
        const storedSessionId = localStorage.getItem(`session_${thingName}`);
        if (storedSessionId) {
            setCurrentSessionId(storedSessionId);
            setHasActiveSession(true);
        } else {
            setCurrentSessionId(null);
            setHasActiveSession(false);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model, serialNumber, thingName]);

    const isProcessing = openingTunnel || closingTunnel;

    return (
        <div className="rs-page-wrapper">
            <div className="rs-header">
                <div className="rs-header-buttons">
                    <button 
                        onClick={handleStartSession} 
                        className="btn btn-primary"
                        disabled={isProcessing || hasActiveSession}
                    >
                        {openingTunnel ? (
                            <>
                                <FontAwesomeIcon icon={faPlay} className="me-1" />
                                Starting Session...
                                <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPlay} className="me-1" />
                                Start Session
                            </>
                        )}
                    </button>
                    
                    <button 
                        onClick={handleEndSession} 
                        className="btn btn-danger"
                        disabled={isProcessing || !hasActiveSession}
                    >
                        {closingTunnel ? (
                            <>
                                <FontAwesomeIcon icon={faStop} className="me-1" />
                                Ending Session...
                                <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faStop} className="me-1" />
                                End Session
                            </>
                        )}
                    </button>
                    
                    <button 
                        onClick={handleRefresh} 
                        className="btn btn-primary"
                        disabled={isProcessing}
                    >
                        <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="rs-content">
                <Grid
                    headers={headers}
                    data={activeSessions}
                    rowsPerPage={50}
                    canMultiSelect={false}
                    showPagination={true}
                    loading={loading}
                    sortable={true}
                />
            </div>
        </div>
    );
};

export default DeviceDetails;
